import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getStatusColor } from 'components/Orders/OrderStatus';
import Flex from 'components/ui/Flex';
import useAppSelector from 'hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { ReturnStatusType } from 'returnCenter-api';
import { returnSelectors } from 'store/reducers/returnSlice';
import styled from 'styled-components';

const FLOW_STATUSES = [
    ReturnStatusType.Created,
    ReturnStatusType.ItemsSelected,
    ReturnStatusType.IncomingPackage,
    ReturnStatusType.Received,
    ReturnStatusType.InProgress,
    ReturnStatusType.CreditError,
    ReturnStatusType.CreditedBatchPending,
    ReturnStatusType.Completed,
] as const;

const FLOW_INDEX: Partial<Record<ReturnStatusType, number>> = {
    [ReturnStatusType.Created]: 0,
    [ReturnStatusType.ItemsSelected]: 1,
    [ReturnStatusType.IncomingPackage]: 2,
    [ReturnStatusType.Received]: 3,
    [ReturnStatusType.InProgress]: 4,
    [ReturnStatusType.CreditError]: 5,
    [ReturnStatusType.CreditedBatchPending]: 6,
    [ReturnStatusType.Completed]: 7,
};

const FLOW_LABELS: Record<ReturnStatusType, string> = {
    [ReturnStatusType.Created]: 'created',
    [ReturnStatusType.ItemsSelected]: 'itemsSelected',
    [ReturnStatusType.IncomingPackage]: 'incomingPackage',
    [ReturnStatusType.Received]: 'received',
    [ReturnStatusType.InProgress]: 'statusInProgress',
    [ReturnStatusType.OnHold]: 'pauseReturn',
    [ReturnStatusType.CreditError]: 'creditError',
    [ReturnStatusType.Processing]: 'processing',
    [ReturnStatusType.CreditedBatchPending]: 'creditedBatchPending',
    [ReturnStatusType.Completed]: 'completed',
    [ReturnStatusType.Cancelled]: 'cancelled',
};

export type StepState = 'done' | 'active' | 'upcoming';

function getStepState(flowStatus: ReturnStatusType, currentStatus: ReturnStatusType | null): StepState {
    if (currentStatus === null || currentStatus === undefined) return 'upcoming';
    if (currentStatus === ReturnStatusType.Completed) return 'done';
    const resolved =
        currentStatus === ReturnStatusType.OnHold ? ReturnStatusType.InProgress :
        currentStatus === ReturnStatusType.CreditError ? ReturnStatusType.CreditError :
        currentStatus;
    const currentIndex = FLOW_INDEX[resolved] ?? 0;
    const stepIndex = FLOW_INDEX[flowStatus] ?? 0;
    if (stepIndex < currentIndex) return 'done';
    if (stepIndex === currentIndex) return 'active';
    return 'upcoming';
}

type ReturnStatusStepperProps = {
    orientation?: 'vertical' | 'horizontal';
    className?: string;
};

const ReturnStatusStepper = ({ orientation = 'vertical', className }: ReturnStatusStepperProps) => {
    const { t } = useTranslation();

    const currentStatus = useAppSelector(returnSelectors.selectReturnSlipStatus);

    const horizontal = orientation === 'horizontal';

    const currentColor = getStatusColor(currentStatus);

    return (
        <Flex column={!horizontal} gap="0.35rem" className={className}>
            {FLOW_STATUSES.map((status, index) => {
                const state = getStepState(status, currentStatus);
                const isLast = index === FLOW_STATUSES.length - 1;
                return (
                    <Flex column={!horizontal} gap="0.35rem" key={status} $grow={horizontal && !isLast ? 1 : undefined}>
                        <Flex column={horizontal} align="center" gap="0.625rem">
                            <StepCircle $state={state} $statusColor={currentColor}>
                                {state === 'done' && <FontAwesomeIcon icon={faCheck} />}
                                {state === 'active' && <ActiveDot $statusColor={currentColor} />}
                            </StepCircle>
                            <StepLabel $state={state} $statusColor={currentColor}>
                                {t(
                                    status === ReturnStatusType.InProgress && currentStatus === ReturnStatusType.OnHold
                                        ? FLOW_LABELS[ReturnStatusType.OnHold]
                                        : FLOW_LABELS[status],
                                )}
                            </StepLabel>
                        </Flex>
                        {!isLast && <StepConnector $horizontal={horizontal} $statusColor={currentColor} />}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export default ReturnStatusStepper;

const StepCircle = styled.div<{ $state: StepState; $statusColor?: string }>`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    background: ${({ $statusColor, $state }) => ($state === 'done' || $state === 'active' ? $statusColor : 'white')};
    color: ${({ $state, theme }) => ($state === 'done' ? theme.colors.fillWhite : theme.colors.text)};
    border: ${({ $state, $statusColor }) => {
        if ($state === 'done') return 'none';
        if ($state === 'active') return `2px solid ${$statusColor}`;
        return `1.5px solid ${$statusColor}`;
    }};
`;

const ActiveDot = styled.div<{ $statusColor: string }>`
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.fillWhite || '#ffffff'};
`;

const StepLabel = styled.span<{ $state: StepState; $statusColor?: string }>`
    font-size: 13px;
    color: ${({ $statusColor }) => $statusColor};
    font-weight: ${({ $state }) => ($state === 'active' ? '500' : '300')};
`;

const StepConnector = styled.div<{ $horizontal: boolean; $statusColor?: string }>`
    ${({ $horizontal, $statusColor }) =>
        $horizontal
            ? `
                height: 2px;
                flex: 1;
                margin-top: 9px;
                background: none;
                border-top: 2px dotted ${$statusColor};
              `
            : `
                width: 2px;
                height: 14px;
                margin-left: 9px;
                background: ${$statusColor};
                `}
`;
